import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'

interface HelpSectionWrapperProps {
  id: string
  icon: React.ComponentType<{ className?: string }>
  title: string
  description: string
  lastUpdated?: string
  isExpanded: boolean
  onToggle: (open: boolean) => void
  children: React.ReactNode
}

function formatDate(dateStr: string): string {
  const parts = dateStr.split('-').map(Number)
  const date = new Date(parts[0] as number, (parts[1] as number) - 1, parts[2] as number)
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function HelpSectionWrapper({
  id,
  icon: Icon,
  title,
  description,
  lastUpdated,
  isExpanded,
  onToggle,
  children,
}: HelpSectionWrapperProps) {
  return (
    <Collapsible open={isExpanded} onOpenChange={onToggle}>
      <Card id={id} className="scroll-mt-20">
        <CollapsibleTrigger asChild>
          <CardHeader className="cursor-pointer hover:bg-muted/50 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 rounded-t-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon className="h-5 w-5 text-muted-foreground" aria-hidden="true" />
                <CardTitle>{title}</CardTitle>
              </div>
              <div className="flex items-center gap-3">
                {lastUpdated && (
                  <span className="text-xs text-muted-foreground whitespace-nowrap hidden sm:inline">
                    Updated {formatDate(lastUpdated)}
                  </span>
                )}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 text-muted-foreground transition-transform duration-200',
                    isExpanded && 'rotate-180'
                  )}
                  aria-hidden="true"
                />
              </div>
            </div>
            <CardDescription>{description}</CardDescription>
          </CardHeader>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="space-y-4">{children}</CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  )
}
